import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getEligibleManagers } from "../../api/account/getEligibleManagers";
import { useTranslationHook } from "../../hooks";
import { User } from "../../types/interfaces/user";
import { enqueueSnackbar } from "notistack";

interface ManagerSelectorProps {
  open: boolean;
  context: "account" | "project" | "activity" | "procedure";
  accountId: number;
  projectId?: number;
  activityId?: number;
  procedureId?: number;
  onSelect: (managerId: number) => void;
  onCancel: () => void;
}

const ManagerSelector = ({
  open,
  context,
  accountId,
  projectId,
  activityId,
  procedureId,
  onSelect,
  onCancel,
}: ManagerSelectorProps) => {
  const { t } = useTranslationHook();

  const [managers, setManagers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchManagers = async () => {
      setLoading(true);
      try {
        let data: User[] = [];
  
        switch (context) {
          case "account":
            data = await getEligibleManagers(accountId);
            break;
          case "project":
            if (projectId) data = await getEligibleManagers(accountId, projectId);
            break;
          case "activity":
            if (projectId && activityId)
              data = await getEligibleManagers(accountId, projectId, activityId);
            break;
          case "procedure":
            if (projectId && activityId && procedureId)
              data = await getEligibleManagers(
                accountId,
                projectId,
                activityId,
                procedureId
              );
            break;
        }
  
        setManagers(data);
        enqueueSnackbar(t("generic.data-loaded"), { variant: "success" });
      } catch (error) {
        enqueueSnackbar(t("error.data-not-loaded"), { variant: "error" });
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchManagers();
  }, [open, context, accountId, projectId, activityId, procedureId]);
  

   const filteredManagers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return managers.filter((m) => m.name.toLowerCase().includes(q));
  }, [searchQuery, managers]);

  let content;

  if (loading) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (filteredManagers.length === 0) {
    content = (
      <Box
        sx={{
          textAlign: "center",
          mt: 6,
          opacity: 0.6,
        }}
      >
        <SearchIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2">
          {t("manager.not-available")}
        </Typography>
      </Box>
    );
  } else {
    content = filteredManagers.map((manager) => (
      <Card
        key={manager.id}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardActionArea onClick={() => onSelect(manager.id)}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600}>
              {manager.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {manager.email}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    ));
  }

  return (
    <Modal open={open} onClose={onCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("manager.select-manager")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("manager.select-manager-description")}
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={t("generic.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ opacity: 0.6 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pb: 2,
          }}
        >
          {content}
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onCancel}>
            {t("generic.cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};


export default ManagerSelector;
